import { z } from 'zod';

type PathSegment = { name: string; index?: number };

const UNSAFE_PATH_KEYS = new Set(['__proto__', 'constructor', 'prototype']);

export const commaSeparatedArray = z.preprocess(
  (value) => (typeof value === 'string' ? (value === '' ? [] : splitCommaList(value)) : value),
  z.array(z.string()),
);

export function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function escapeCommaListItem(item: string): string {
  return item.replace(/[\\,]/g, (char) => `\\${char}`);
}

function splitCommaList(value: string): string[] {
  const items: string[] = [];
  let current = '';
  for (let index = 0; index < value.length; index++) {
    const char = value[index];
    if (char === '\\' && index + 1 < value.length) {
      current += value[++index];
      continue;
    }
    if (char === ',') {
      items.push(current);
      current = '';
      continue;
    }
    current += char;
  }
  items.push(current);
  return items;
}

function setLeaf(params: URLSearchParams, path: string, value: unknown): void {
  if (value === undefined || value === null) return;
  if (Array.isArray(value)) {
    if (value.length === 0) return;
    if (value.every((item) => !isPlainObject(item) && !Array.isArray(item))) {
      params.set(path, value.map((item) => escapeCommaListItem(String(item))).join(','));
      return;
    }
    value.forEach((item, index) => {
      setLeaf(params, `${path}[${index}]`, item);
    });
    return;
  }
  if (isPlainObject(value)) {
    for (const [key, nested] of Object.entries(value)) setLeaf(params, `${path}.${key}`, nested);
    return;
  }
  params.set(path, String(value));
}

export function stringifySearch(search: Record<string, unknown>): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(search)) setLeaf(params, key, value);
  const query = params.toString().replaceAll('%2C', ',');
  return query ? `?${query}` : '';
}

function parsePathSegment(part: string): PathSegment {
  const match = /^([^[]+)(?:\[(\d+)\])?$/.exec(part);
  if (!match?.[1]) return { name: part };
  const [, name, index] = match;
  return index === undefined ? { name } : { name, index: Number(index) };
}

function setPath(target: Record<string, unknown>, path: PathSegment[], value: unknown): void {
  const [head, ...rest] = path;
  if (!head || UNSAFE_PATH_KEYS.has(head.name)) return;
  if (head.index === undefined) {
    if (rest.length === 0) {
      target[head.name] = value;
      return;
    }
    const current = target[head.name];
    const nested = isPlainObject(current) ? current : {};
    target[head.name] = nested;
    setPath(nested, rest, value);
    return;
  }
  const currentArray = target[head.name];
  const array: unknown[] = Array.isArray(currentArray) ? currentArray : [];
  target[head.name] = array;
  if (rest.length === 0) {
    array[head.index] = value;
    return;
  }
  const currentItem = array[head.index];
  const nested = isPlainObject(currentItem) ? currentItem : {};
  array[head.index] = nested;
  setPath(nested, rest, value);
}

export function parseSearch(searchStr: string): Record<string, unknown> {
  const params = new URLSearchParams(searchStr[0] === '?' ? searchStr.slice(1) : searchStr);
  const result: Record<string, unknown> = {};
  for (const [key, value] of params.entries()) {
    setPath(result, key.split('.').map(parsePathSegment), value);
  }
  return result;
}
